import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, ArrowRight, ArrowDownRight, X } from 'lucide-react';

// Single tour step card — a ws-tile sitting on top of the dim overlay,
// positioned next to a "spotlighted" DOM target. The OnboardingTour
// parent picks the target via a CSS selector + arrow direction;
// TourStep just measures the target each frame (resize, scroll) and
// reflows.
//
// Why measure ourselves instead of CSS-positioning relative to the
// target? The target is in normal flow, often inside .ws-header which
// is position:sticky. A portal-rendered overlay can't be a sibling of
// the target, so we resolve coordinates from getBoundingClientRect()
// every animation frame.

export type ArrowDirection = 'up-right' | 'right' | 'down-right' | 'down' | 'left' | 'up' | 'none';

interface TourStepProps {
  /** 1-based for display. */
  stepNumber: number;
  totalSteps: number;
  title: string;
  /** Multiline body; rendered as <p> nodes. */
  body: string[];
  /** Optional CSS selector to spotlight. If absent the card centers itself. */
  targetSelector?: string;
  /** Where the arrow points (relative to the card, towards the target). */
  arrow?: ArrowDirection;
  /** Primary action label — defaults to "Next". */
  primaryLabel?: string;
  onPrimary: () => void;
  /** Secondary action label — defaults to "Skip". */
  secondaryLabel?: string;
  onSecondary: () => void;
  /** Optional explicit close (Esc / X). Default: same as secondary. */
  onClose?: () => void;
}

interface TargetRect { top: number; left: number; width: number; height: number; }

function measure(selector?: string): TargetRect | null {
  if (!selector) return null;
  const el = document.querySelector(selector) as HTMLElement | null;
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

const ArrowIcon = ({ dir }: { dir: ArrowDirection }) => {
  if (dir === 'up-right')   return <ArrowUpRight size={18} strokeWidth={2} />;
  if (dir === 'right')      return <ArrowRight size={18} strokeWidth={2} />;
  if (dir === 'down-right') return <ArrowDownRight size={18} strokeWidth={2} />;
  return null;
};

export function TourStep({
  stepNumber, totalSteps, title, body,
  targetSelector, arrow = 'none',
  primaryLabel = 'Next', onPrimary,
  secondaryLabel = 'Skip', onSecondary,
  onClose,
}: TourStepProps) {
  const { t } = useTranslation('common');
  const [rect, setRect] = useState<TargetRect | null>(() => measure(targetSelector));

  // Re-measure on every reasonable trigger: window resize, scroll, and
  // a slow tick to catch sticky-header reflows. Costs effectively
  // nothing and avoids a stale spotlight when the user resizes mid-tour.
  useEffect(() => {
    if (!targetSelector) { setRect(null); return; }
    let alive = true;
    const tick = () => { if (alive) setRect(measure(targetSelector)); };
    tick();
    const interval = window.setInterval(tick, 250);
    window.addEventListener('resize', tick);
    window.addEventListener('scroll', tick, true);
    return () => {
      alive = false;
      window.clearInterval(interval);
      window.removeEventListener('resize', tick);
      window.removeEventListener('scroll', tick, true);
    };
  }, [targetSelector]);

  // Card placement. If we have a target rect, anchor near it; if not, center.
  // CRITICAL: always clamp the WHOLE card inside the viewport. The previous
  // version only floored `right` and capped `top`, so a target that wasn't at
  // the top-right (or a small viewport) pushed the card's left/bottom edge off
  // screen — the reported "Step 2 of 3 is off screen". We position with left/top
  // (easier to clamp) and bound both axes by the card's real size.
  const CARD_W = 360;
  const CARD_H = 340;   // generous estimate (title + 2 paragraphs + buttons)
  const MARGIN = 16;
  const cardStyle: React.CSSProperties = (() => {
    if (!rect) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    const rectRight  = rect.left + rect.width;
    const rectBottom = rect.top  + rect.height;
    // Prefer: below the target, right-aligned to it (reads up-right to a toolbar).
    let left = rectRight - CARD_W;
    let top  = rectBottom + 24;
    // Not enough room below → flip above the target.
    if (top + CARD_H > window.innerHeight - MARGIN) {
      top = rect.top - CARD_H - 24;
    }
    // Clamp both axes so the entire card stays on screen.
    const maxLeft = Math.max(MARGIN, window.innerWidth  - CARD_W - MARGIN);
    const maxTop  = Math.max(MARGIN, window.innerHeight - CARD_H - MARGIN);
    left = Math.min(Math.max(MARGIN, left), maxLeft);
    top  = Math.min(Math.max(MARGIN, top),  maxTop);
    return { top: `${top}px`, left: `${left}px` };
  })();

  // Spotlight ring drawn via two stacked layers (matches .ws-spotlight
  // family in app.css): a darkening veil with a punched-out rect, and a
  // bright outline for precision. We assemble inline because the rect
  // is per-step. PAD adds breathing room around the target.
  const PAD = 8;
  const spotlight = rect ? {
    top:    rect.top - PAD,
    left:   rect.left - PAD,
    width:  rect.width + PAD * 2,
    height: rect.height + PAD * 2,
  } : null;

  return (
    <>
      {/* Dim veil — covers the whole screen, then a transparent rect is
          drawn over the target via clip-path so the target reads as
          "lit". pointer-events: none so the user can still click the
          target if the step asks them to. */}
      <div
        aria-hidden
        className="fixed inset-0 z-[90] pointer-events-none ws-anim-fade-up"
        style={{
          animationDuration: '180ms',
          background: spotlight
            ? `radial-gradient(circle at ${spotlight.left + spotlight.width / 2}px ${spotlight.top + spotlight.height / 2}px, transparent 0, transparent ${Math.max(spotlight.width, spotlight.height) * 0.55}px, rgba(0,0,0,0.52) ${Math.max(spotlight.width, spotlight.height) * 0.9}px)`
            : 'rgba(0,0,0,0.42)',
        }}
      />
      {/* Bright outline rect around the target — only when we have a target. */}
      {spotlight && (
        <div
          aria-hidden
          className="fixed z-[91] pointer-events-none rounded-md ws-anim-fade-up"
          style={{
            top: spotlight.top, left: spotlight.left,
            width: spotlight.width, height: spotlight.height,
            border: '2px solid var(--ws-accent)',
            boxShadow: '0 0 0 4px color-mix(in oklch, var(--ws-accent) 18%, transparent)',
            animationDuration: '220ms',
          }}
        />
      )}

      {/* The tour card itself. ws-tile + max-w-sm + shadow-lg per design spec.
          A11Y-1 — deliberately NOT `role="dialog" aria-modal="true"` and NOT
          given a focus trap. A real modal traps Tab and makes the page
          behind it inert; this coach-mark's veil is `pointer-events: none`
          (see above) SO THE USER CAN STILL OPERATE THE SPOTLIGHTED PAGE —
          that's the whole point of a "point at this button, now click it"
          tour step. `aria-modal="true"` was a false promise: screen-reader
          users would have been told the background was inert when it very
          much wasn't, and a Tab-trap here would have made it impossible to
          reach the very control the step is pointing at. `role="group"`
          keeps `aria-labelledby` meaningful (an accessible name on a
          non-interactive container) without claiming modal semantics. */}
      <div
        role="group"
        aria-labelledby={`ws-tour-title-${stepNumber}`}
        className="fixed z-[92] ws-tile max-w-sm w-[360px] p-5 ws-anim-fade-up"
        style={{
          ...cardStyle,
          boxShadow: 'var(--ws-shadow-lg)',
          animationDuration: '220ms',
        }}
      >
        <button
          type="button"
          aria-label="Close tour"
          onClick={() => (onClose ?? onSecondary)()}
          className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full inline-flex items-center justify-center text-ws-ink-3 hover:text-ws-ink hover:bg-ws-surface-2"
        >
          <X size={14} strokeWidth={2} />
        </button>

        <div className="flex items-center justify-between mb-3 pr-6">
          <span
            aria-hidden
            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-xs font-semibold"
            style={{ background: 'var(--ws-accent)' }}
          >
            {stepNumber}
          </span>
          <span
            className="text-[11px] uppercase text-ws-ink-3 tracking-wider"
            style={{ fontFamily: 'var(--ws-font-mono)' }}
          >
            {t('common:onboarding.stepCounter', { current: stepNumber, total: totalSteps })}
          </span>
        </div>

        <h2
          id={`ws-tour-title-${stepNumber}`}
          className="text-base font-semibold text-ws-ink leading-tight flex items-center gap-2"
        >
          {arrow !== 'none' && (
            <span className="text-ws-accent inline-flex" aria-hidden>
              <ArrowIcon dir={arrow} />
            </span>
          )}
          {title}
        </h2>

        <div className="mt-2 space-y-2">
          {body.map((p, i) => (
            <p key={i} className="text-sm text-ws-ink-2 leading-relaxed">{p}</p>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            className="ws-btn-tertiary text-sm"
            onClick={onSecondary}
          >
            {secondaryLabel}
          </button>
          <button
            type="button"
            className="ws-btn-primary text-sm px-4 py-2"
            onClick={onPrimary}
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </>
  );
}
